import { AfterViewInit, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { ConfigService } from 'src/app/shared/services/config.service';
import { ErrorDialogService } from 'src/app/shared/errors/error-dialog.service';
import { DirtyComponent } from 'src/app/core/components/errors/dirty-check.component';
import {
  exportCustomerInvoiceItemsToRemove,
  mandatoryLineFields,
} from '../custom-mapping/config/mapping-config';

@Component({
  selector: 'app-custom-lineitem-mapping',
  templateUrl: './custom-lineitem-mapping.component.html',
  styleUrls: ['./custom-lineitem-mapping.component.css'],
})
export class CustomLineitemMappingComponent implements OnInit, AfterViewInit, DirtyComponent {
  mappedFields = mandatoryLineFields;
  unMappedFields = ['Blank', ...exportCustomerInvoiceItemsToRemove];
  public lineItemMappingForm: FormGroup;
  specMode: string = 'Create_New';
  mappingOptions = [];
  mandatoryFields = [];
  selectedSpec;
  isDirty = false;
  @Output() dirtyEvent = new EventEmitter<boolean>();

  constructor(
    private fb: FormBuilder,
    private configService: ConfigService,
    private errorDialogService: ErrorDialogService
  ) {}

  ngOnInit(): void {
    this.mandatoryFields = [...mandatoryLineFields];
    this.unMappedFields = ['Blank', ...exportCustomerInvoiceItemsToRemove];
    this.mappedFields = [...this.mandatoryFields];

    this.initializationformGroup();
    this.getMappingOptions();
  }

  ngAfterViewInit() {
    this.isLineItemMappingUnChanged();
  }

  isLineItemMappingUnChanged() {
    setTimeout(() => {
      setTimeout(
        function () {
          this.lineItemMappingForm.valueChanges.subscribe(() => {
            this.isDirty = true;
            this.dirtyEvent.emit(this.isDirty);
          });
        }.bind(this),
        0
      );
    }, 1000);
  }

  initializationformGroup() {
    this.lineItemMappingForm = this.fb.group({
      specifications_id: ['', Validators.required],
      delimiter: ['', Validators.required],
      headers: ['', Validators.required],
    });
  }

  drop(event: CdkDragDrop<string[]>) {
    this.isDirty = true;
    this.dirtyEvent.emit(this.isDirty);
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      if (this.isFieldMandatory(event.previousContainer.data[event.previousIndex])) {
        return;
      } else {
        const previousIndexValue = event.previousContainer.data[event.previousIndex];
        if (previousIndexValue === 'Blank' && event.previousContainer.id === 'cdk1') {
          this.mappedFields.splice(event.currentIndex, 0, previousIndexValue);
          return;
        }
        if (previousIndexValue === 'Blank' && event.previousContainer.id === 'cdk2') {
          this.mappedFields.splice(event.previousIndex, 1);
          return;
        }

        transferArrayItem(
          event.previousContainer.data,
          event.container.data,
          event.previousIndex,
          event.currentIndex
        );
      }
    }
  }

  isFieldMandatory(field) {
    return this.mandatoryFields.includes(field);
  }

  specModeChangeEvent(event) {
    this.specMode = event?.value;
    this.selectedSpec = '';
    this.lineItemMappingForm.reset();
    this.mappedFields = [];
    if (this.specMode === 'Create_New') {
      this.unMappedFields = ['Blank', ...exportCustomerInvoiceItemsToRemove];
      this.mappedFields = [...this.mandatoryFields];
    } else {
      this.unMappedFields = ['Blank'];
      this.mappedFields = [];
    }
  }

  specSelectEvent(spec) {
    this.lineItemMappingForm.reset();
    this.getSelectMappingOption(spec.value.specifications_id);
  }

  isSpecEditMode() {
    return this.specMode === 'Edit';
  }

  getMappingOptions() {
    const requestBody = { config_queries: [{ config_id: 'customer_invoice_specifications' }] };
    this.configService.getLineItemsSpecifications(requestBody).subscribe(data => {
      this.mappingOptions = data[0];
      this.isDirty = false;
      this.dirtyEvent.emit(this.isDirty);
    });
  }

  getSelectMappingOption(specId) {
    this.lineItemMappingForm.reset();
    const requestBody = {
      config_queries: [
        {
          config_id: 'customer_invoice_specifications',
          specifications_id: specId,
        },
      ],
    };
    this.configService.getLineItemsSpecifications(requestBody).subscribe(response => {
      this.mappedFields = [];
      this.mappedFields = response[0].data[0].csvColumns.map(function (item) {
        return item === 'TBA' ? 'Blank' : item;
      });

      this.lineItemMappingForm.patchValue({ specifications_id: response[0]?.specifications_id });
      this.lineItemMappingForm.patchValue({ delimiter: response[0]?.data[0]?.delimiter });
      this.lineItemMappingForm.patchValue({ headers: response[0]?.data[0]?.headers });
      this.unMappedFields = response[0].data[0].availableColumns.map(function (item) {
        return item === 'TBA' ? 'Blank' : item;
      });
    });
  }

  getAllShipFields(mappingData) {
    const shipFields = [];

    for (const mappingObj of mappingData) {
      const fieldName = mappingObj.field_name;
      shipFields.push(fieldName);
    }
    return shipFields;
  }

  filterTwoArrayNotIncluded(arr1, arr2) {
    const unMappedWithBlank = ['Blank'];
    const arrayUnmapped = arr1.filter(x => !arr2.includes(x));
    return unMappedWithBlank.concat(arrayUnmapped);
  }

  saveLineItemConfigMapping() {
    if (!this.lineItemMappingForm.valid) {
      this.errorDialogService.openDialog('alert.enterAllMandatory', false);
      return;
    }

    const blankCheckedMapFields = this.mappedFields.map(function (item) {
      return item === 'Blank' ? 'TBA' : item;
    });

    const dataFields = {
      delimiter: this.lineItemMappingForm.value.delimiter.trim(),
      availableColumns: this.unMappedFields,
      csvColumns: blankCheckedMapFields,
      headers: this.lineItemMappingForm.value.headers,
    };

    const options = {
      config_id: 'customer_invoice_specifications',
      specifications_id: this.lineItemMappingForm.value.specifications_id,
      use_for_automated_upload: true,
      data: dataFields,
    };

    this.configService.saveMappingOption(options).subscribe(() => {
      this.getMappingOptions();
      this.lineItemMappingForm.reset();
      if (this.specMode === 'Create_New') {
        this.mappedFields = [...this.mandatoryFields];
        this.unMappedFields = ['Blank', ...exportCustomerInvoiceItemsToRemove];
      } else {
        this.mappedFields = [];
        this.unMappedFields = ['Blank'];
      }
    });
  }

  canDeactivate() {
    return this.isDirty;
  }
}
